/**
 * ZodiacGlyph - Elegant Gold Gradient SVG Zodiac Symbols
 * 
 * Luxurious, mystical zodiac glyphs with:
 * - Gold-to-amber gradients with inner glow
 * - Elegant, detailed paths inspired by traditional astrological symbols
 * - Subtle animation-ready design
 */
import { memo } from 'react';

// Unique gradient ID generator to avoid conflicts
const getGradientId = (sign, instance) => `zodiac-gradient-${sign}-${instance || Math.random().toString(36).substr(2, 9)}`;

/**
 * SVG paths for each zodiac sign - elegant traditional glyph style
 * These paths are designed to be beautiful and mystical while remaining recognizable
 */
const ZODIAC_PATHS = {
    // Aries - Ram's horns curving upward
    aries: {
        path: "M12 22V12M12 12C12 12 6 12 6 6C6 3 8 2 10 2C12 2 12 5 12 5M12 12C12 12 18 12 18 6C18 3 16 2 14 2C12 2 12 5 12 5",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Taurus - Bull horns with circle
    taurus: {
        path: "M4 6C4 4 6 2 8 2C10 2 11 3 12 4C13 3 14 2 16 2C18 2 20 4 20 6M12 4V8M12 12C15.3137 12 18 14.6863 18 18C18 21.3137 15.3137 24 12 24C8.68629 24 6 21.3137 6 18C6 14.6863 8.68629 12 12 12Z",
        viewBox: "0 0 24 26",
        strokeWidth: 1.8
    },
    // Gemini - Twin pillars with connecting bars
    gemini: {
        path: "M6 2C6 2 6 4 8 4C10 4 14 4 16 4C18 4 18 2 18 2M6 22C6 22 6 20 8 20C10 20 14 20 16 20C18 20 18 22 18 22M8 4V20M16 4V20",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Cancer - Crab claws, yin-yang style
    cancer: {
        path: "M18 9C18 9 20 9 20 7C20 5 18 5 16 5C14 5 12 7 12 9C12 11 14 13 16 13C18 13 18 11 18 11M6 15C6 15 4 15 4 17C4 19 6 19 8 19C10 19 12 17 12 15C12 13 10 11 8 11C6 11 6 13 6 13",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Leo - Lion's mane spiral with tail
    leo: {
        path: "M8 8C8 8 6 8 6 6C6 4 8 2 12 2C16 2 18 4 18 8C18 12 14 14 14 14C14 14 16 14 16 16C16 18 14 20 12 20C10 20 8 18 8 18M12 14V20M12 20C12 20 14 22 16 22",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Virgo - Maiden with crossed legs, M-shape with loop
    virgo: {
        path: "M4 4V14C4 17 6 18 8 18M8 4V18M12 4V14C12 14 12 18 16 18C20 18 20 14 20 14M20 14V22M20 18L22 16",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Libra - Balanced scales with horizon line
    libra: {
        path: "M4 20H20M12 20V12M8 12H16M8 12C8 9 9.5 6 12 6C14.5 6 16 9 16 12M6 8C6 8 8 6 8 6M18 8C18 8 16 6 16 6",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Scorpio - M with arrow tail (stinger)
    scorpio: {
        path: "M4 4V14C4 17 6 18 8 18M8 4V18M12 4V14C12 17 14 18 16 18M16 4V18L20 14M20 14L20 20M18 18L20 20L22 18",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Sagittarius - Arrow pointing upward
    sagittarius: {
        path: "M4 20L20 4M20 4H12M20 4V12M8 12L16 20",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Capricorn - Sea-goat with curled tail
    capricorn: {
        path: "M4 6C4 4 6 2 8 2C10 2 10 4 10 6V16C10 18 12 20 14 20C16 20 18 18 18 16C18 14 16 12 14 12C12 12 10 14 10 16M18 16C18 16 20 18 20 20C20 22 18 22 18 22",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Aquarius - Water waves (two parallel wavy lines)
    aquarius: {
        path: "M2 10C4 8 6 12 8 10C10 8 12 12 14 10C16 8 18 12 20 10C22 8 22 10 22 10M2 16C4 14 6 18 8 16C10 14 12 18 14 16C16 14 18 18 20 16C22 14 22 16 22 16",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    },
    // Pisces - Two fish swimming in opposite directions
    pisces: {
        path: "M8 2C8 2 4 6 4 12C4 18 8 22 8 22M16 2C16 2 20 6 20 12C20 18 16 22 16 22M4 12H20",
        viewBox: "0 0 24 24",
        strokeWidth: 1.8
    }
};

/**
 * ZodiacGlyph Component
 * 
 * @param {string} sign - Zodiac sign name (e.g., 'capricorn', 'aries')
 * @param {number} size - Size in pixels (default: 24)
 * @param {string} variant - 'gold' | 'silver' | 'rose' | 'cosmic' (default: 'gold')
 * @param {boolean} glow - Add glow effect (default: true)
 * @param {string} instance - Unique instance ID for gradient (for multiple same signs)
 * @param {string} className - Additional CSS classes
 */
const ZodiacGlyph = memo(({ 
    sign, 
    size = 24, 
    variant = 'gold',
    glow = true,
    instance = null,
    className = ''
}) => {
    const signKey = sign?.toLowerCase();
    const signData = ZODIAC_PATHS[signKey];
    
    if (!signData) {
        // Fallback to Unicode symbol if sign not found
        const fallbackSymbols = {
            aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
            leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
            sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓'
        };
        return (
            <span 
                className={className}
                style={{ 
                    fontSize: size, 
                    background: 'linear-gradient(135deg, #F5D76E 0%, #D4AF37 50%, #AA8C2C 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: glow ? 'drop-shadow(0 0 4px rgba(212, 175, 55, 0.5))' : 'none'
                }}
            >
                {fallbackSymbols[signKey] || '✧'}
            </span>
        );
    }
    
    const gradientId = getGradientId(signKey, instance);
    const glowId = `glow-${gradientId}`;
    const innerGlowId = `inner-glow-${gradientId}`;
    
    // Color variants - luxurious palette
    const gradients = {
        gold: {
            start: '#FEF3C7',      // Warm cream gold
            mid: '#F5D76E',        // Bright gold
            end: '#B8860B',        // Deep gold
            glow: 'rgba(245, 215, 110, 0.7)',
            innerGlow: 'rgba(254, 243, 199, 0.4)'
        },
        silver: {
            start: '#F8FAFC',
            mid: '#CBD5E1',
            end: '#64748B',
            glow: 'rgba(203, 213, 225, 0.6)',
            innerGlow: 'rgba(248, 250, 252, 0.4)'
        },
        rose: {
            start: '#FECDD3',
            mid: '#FB7185',
            end: '#BE123C',
            glow: 'rgba(251, 113, 133, 0.6)',
            innerGlow: 'rgba(254, 205, 211, 0.4)'
        },
        cosmic: {
            start: '#C4B5FD',
            mid: '#8B5CF6',
            end: '#5B21B6',
            glow: 'rgba(139, 92, 246, 0.6)',
            innerGlow: 'rgba(196, 181, 253, 0.4)'
        }
    };
    
    const colors = gradients[variant] || gradients.gold;
    
    return (
        <svg 
            width={size} 
            height={size} 
            viewBox={signData.viewBox || "0 0 24 24"}
            fill="none"
            className={className}
            style={{ 
                filter: glow ? `drop-shadow(0 0 ${size/4}px ${colors.glow})` : 'none'
            }}
            aria-label={`${sign} zodiac symbol`}
        >
            <defs>
                {/* Main gradient - 3-stop for depth */}
                <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={colors.start} />
                    <stop offset="50%" stopColor={colors.mid} />
                    <stop offset="100%" stopColor={colors.end} />
                </linearGradient>
                
                {/* Inner glow gradient for highlight effect */}
                <linearGradient id={innerGlowId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={colors.innerGlow} />
                    <stop offset="100%" stopColor="transparent" />
                </linearGradient>
                
                {/* Glow filter */}
                {glow && (
                    <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="1.5" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                )}
            </defs>
            
            {/* Main glyph path with gradient stroke */}
            <path 
                d={signData.path}
                stroke={`url(#${gradientId})`}
                strokeWidth={signData.strokeWidth || 1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={glow ? `url(#${glowId})` : undefined}
            />
        </svg>
    );
});

ZodiacGlyph.displayName = 'ZodiacGlyph';

export default ZodiacGlyph;

/**
 * ZodiacGlyphCircle - Zodiac glyph inside an elegant circular frame
 * Perfect for headers and prominent displays
 */
export const ZodiacGlyphCircle = memo(({ 
    sign, 
    size = 48, 
    variant = 'gold',
    showFrame = true,
    animated = false,
    className = ''
}) => {
    const colors = {
        gold: {
            border: 'rgba(212, 175, 55, 0.5)',
            borderHighlight: 'rgba(245, 215, 110, 0.8)',
            bg: 'rgba(212, 175, 55, 0.08)',
            glow: 'rgba(212, 175, 55, 0.4)',
            innerGlow: 'rgba(245, 215, 110, 0.15)'
        },
        silver: {
            border: 'rgba(148, 163, 184, 0.5)',
            borderHighlight: 'rgba(226, 232, 240, 0.8)',
            bg: 'rgba(148, 163, 184, 0.08)',
            glow: 'rgba(148, 163, 184, 0.3)',
            innerGlow: 'rgba(226, 232, 240, 0.1)'
        },
        cosmic: {
            border: 'rgba(139, 92, 246, 0.5)',
            borderHighlight: 'rgba(196, 181, 253, 0.8)',
            bg: 'rgba(139, 92, 246, 0.08)',
            glow: 'rgba(139, 92, 246, 0.3)',
            innerGlow: 'rgba(196, 181, 253, 0.1)'
        }
    };
    
    const c = colors[variant] || colors.gold;
    
    return (
        <div 
            className={`flex items-center justify-center ${className} ${animated ? 'animate-pulse-slow' : ''}`}
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: showFrame ? `1.5px solid ${c.border}` : 'none',
                background: showFrame 
                    ? `radial-gradient(circle at 30% 30%, ${c.innerGlow} 0%, ${c.bg} 50%, transparent 100%)`
                    : 'transparent',
                boxShadow: showFrame 
                    ? `0 0 ${size/3}px ${c.glow}, inset 0 0 ${size/4}px ${c.innerGlow}, 0 1px 0 ${c.borderHighlight}`
                    : 'none',
                position: 'relative'
            }}
        >
            {/* Subtle ring highlight */}
            {showFrame && (
                <div 
                    style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        background: `conic-gradient(from 180deg, transparent 0%, ${c.borderHighlight} 10%, transparent 20%)`,
                        opacity: 0.5
                    }}
                />
            )}
            <ZodiacGlyph 
                sign={sign} 
                size={size * 0.55} 
                variant={variant}
                glow={true}
            />
        </div>
    );
});

ZodiacGlyphCircle.displayName = 'ZodiacGlyphCircle';

/**
 * Export all zodiac sign keys for mapping
 */
export const ZODIAC_SIGNS = [
    'aries', 'taurus', 'gemini', 'cancer',
    'leo', 'virgo', 'libra', 'scorpio',
    'sagittarius', 'capricorn', 'aquarius', 'pisces'
];
